import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { GreekFlag } from "./GreekFlag";
import { CONTACT } from "../data/menu";

export const SouvlakiFeature = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);

  return (
    <section id="souvlaki" ref={ref} data-testid="souvlaki-feature" className="relative overflow-hidden py-20 lg:py-24" style={{ background: "var(--ice)" }}>
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left — copy + CTAs */}
        <div>
          <div className="flex items-center gap-3 mb-5">
            <GreekFlag className="w-9 h-auto" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--slate)]">
              In-store now · Donvale
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] tracking-tight leading-[0.95]">
            Greek Traditional <span className="italic text-[var(--azure)]">Souvláki.</span>
          </h2>
          <p className="mt-6 max-w-md text-[var(--slate)] text-base lg:text-lg leading-relaxed">
            Char-grilled lamb & chicken, hand-wrapped in warm pita with golden
            chips, red onion and herbs — authentic Greek flavours, made the
            traditional way.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={CONTACT.orderUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="souvlaki-order-btn"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-white text-[13px] font-mono tracking-[0.15em] uppercase transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              style={{ background: "var(--azure)" }}
            >
              <ShoppingBag size={15} strokeWidth={2.4} />
              Online Order
            </a>
            <a
              href="#cat-gyros"
              data-testid="souvlaki-menu-btn"
              className="rounded-full px-7 py-3.5 text-white text-[13px] font-mono tracking-[0.15em] uppercase transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              style={{ background: "var(--navy)" }}
            >
              See the Souvláki menu
            </a>
          </div>
        </div>

        {/* Right — banner */}
        <motion.div
          initial={{ opacity: 0, y: 40, clipPath: "inset(8% 0% 0% 0%)" }}
          whileInView={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0%)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-[3px]"
          style={{ boxShadow: "0 40px 90px -50px rgba(0,42,84,0.55)", border: "1px solid var(--hair)" }}
        >
          <motion.img
            src="/photos/souvlaki-banner-clean.jpg"
            alt="Lucky Corner Greek Traditional Souvlaki"
            style={{ scale: imgScale }}
            className="w-full h-auto block"
          />
        </motion.div>
      </div>
    </section>
  );
};
